import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GripVertical, Search } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/api/client';
import { MultiSelectFilter } from '@/components/ui/MultiSelectFilter';
import { Lead, LeadStatus, money, ratingClass, dateTime, useLeadUsers } from './leads.api';
import { LeadContactButtons } from './LeadContactButtons';
import { LeadDetailDrawer } from './LeadDetailDrawer';
import { LeadsSubNav } from './LeadsSubNav';

/**
 * Lead Pipeline — one column per status, drag a card to move the deal.
 *
 * Drag-and-drop uses the browser's own HTML5 events rather than a library: the board is the only
 * place in the app that needs it, and the whole interaction is "pick up a card, drop it on a
 * column". A drop posts `POST /leads/:id/move`, which is the same status change the edit form
 * makes, so the activity trail reads identically either way.
 */
interface PipelineColumn {
  status: LeadStatus;
  count: number;
  value: number;
  hasMore: boolean;
  leads: Lead[];
}

export function LeadsPipelinePage() {
  const qc = useQueryClient();
  const { data: users } = useLeadUsers();
  const [q, setQ] = useState('');
  const [ownerIds, setOwnerIds] = useState<(string | number)[]>([]);
  const [dragId, setDragId] = useState<number | null>(null);
  const [overStatus, setOverStatus] = useState<number | null>(null);
  const [detailId, setDetailId] = useState<number | null>(null);

  const params = useMemo(() => {
    const p: any = { limit: 50 };
    if (q) p.q = q;
    if (ownerIds.length) p.ownerIds = ownerIds.join(',');
    return p;
  }, [q, ownerIds]);

  const { data, isLoading } = useQuery({
    queryKey: ['lead-pipeline', params],
    queryFn: async () => (await api.get<{ columns: PipelineColumn[]; totals: { leads: number; value: number } }>('/leads/pipeline', { params })).data,
  });

  const move = useMutation({
    mutationFn: async (v: { id: number; statusId: number | null }) => (await api.post(`/leads/${v.id}/move`, { statusId: v.statusId })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lead-pipeline'] });
      qc.invalidateQueries({ queryKey: ['leads'] });
      qc.invalidateQueries({ queryKey: ['lead-dashboard'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not move the lead'),
  });

  const drop = (statusId: number) => {
    setOverStatus(null);
    if (!dragId) return;
    // `0` is the synthetic "No status" column — dropping there clears the status rather than
    // trying to save a status id that does not exist.
    move.mutate({ id: dragId, statusId: statusId === 0 ? null : statusId });
    setDragId(null);
  };

  return (
    <div className="p-6">
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">Lead Pipeline</h1>
          <p className="text-xs text-gray-500 mt-0.5">
            {(data?.totals.leads ?? 0).toLocaleString('en-IN')} leads · {money(data?.totals.value)} of expected business. Drag a card to change its stage.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="text-xs text-gray-500 block">Sale Person</label>
            <MultiSelectFilter label="" placeholder="Everyone" values={ownerIds} onChange={setOwnerIds}
              options={[{ value: -1, label: 'Unassigned' }, ...(users ?? []).map((u) => ({ value: u.id, label: u.name }))]} />
          </div>
          <div>
            <label className="text-xs text-gray-500 block">Search</label>
            <div className="relative">
              <Search size={13} className="absolute left-2 top-2.5 text-gray-400" />
              <input className="border rounded pl-7 pr-2 py-1.5 text-sm dark:bg-slate-900" placeholder="Company / mobile"
                value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
          </div>
        </div>
      </div>

      <LeadsSubNav />

      {isLoading ? (
        <div className="text-center py-16 text-gray-400">Loading…</div>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-4 items-start">
          {(data?.columns ?? []).map((col) => (
            <div key={col.status.id}
              onDragOver={(e) => { e.preventDefault(); setOverStatus(col.status.id); }}
              onDragLeave={() => setOverStatus((s) => (s === col.status.id ? null : s))}
              onDrop={() => drop(col.status.id)}
              className={`w-[280px] shrink-0 rounded-lg border bg-slate-50 dark:bg-slate-800 ${
                overStatus === col.status.id ? 'ring-2 ring-emerald-400 bg-emerald-50/60' : ''}`}>
              <div className="px-3 py-2 border-b sticky top-0 bg-slate-50 dark:bg-slate-800 rounded-t-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold flex items-center gap-1.5">
                    <span className="inline-block w-2 h-2 rounded-full" style={{ background: col.status.colour ?? '#94a3b8' }} />
                    {col.status.name}
                  </span>
                  <span className="text-xs text-gray-500">{col.count}</span>
                </div>
                <div className="text-[11px] text-gray-400">{money(col.value)}</div>
              </div>

              <div className="p-2 space-y-2 max-h-[65vh] overflow-y-auto">
                {col.leads.map((l) => (
                  <div key={l.id} draggable
                    onDragStart={() => setDragId(l.id)}
                    onDragEnd={() => { setDragId(null); setOverStatus(null); }}
                    onClick={() => setDetailId(l.id)}
                    className={`bg-white dark:bg-slate-900 border rounded p-2 cursor-pointer hover:border-emerald-300 ${dragId === l.id ? 'opacity-50' : ''}`}>
                    <div className="flex items-start justify-between gap-1">
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{l.companyName}</div>
                        <div className="text-[11px] text-gray-400 truncate">{l.contactName || l.leadNo}</div>
                      </div>
                      <GripVertical size={12} className="text-slate-300 shrink-0 mt-0.5" />
                    </div>
                    <div className="flex flex-wrap items-center gap-1 mt-1.5">
                      {l.rating && <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${ratingClass(l.rating)}`}>{l.rating}</span>}
                      {l.serviceType && <span className="text-[10px] px-1.5 py-0.5 rounded-full border bg-slate-50 text-slate-600 border-slate-200">{l.serviceType}</span>}
                      {!!l.monthlyOrders && <span className="text-[10px] text-gray-500">{l.monthlyOrders.toLocaleString('en-IN')}/mo</span>}
                    </div>
                    {!!l.estimatedValue && <div className="text-[11px] text-emerald-700 mt-1">{money(l.estimatedValue)}</div>}
                    {l.nextFollowupAt && (
                      <div className={`text-[10px] mt-1 ${new Date(l.nextFollowupAt) < new Date() ? 'text-rose-600' : 'text-gray-400'}`}>
                        Next: {dateTime(l.nextFollowupAt)}
                      </div>
                    )}
                    <div className="mt-1.5 flex items-center justify-between" onClick={(e) => e.stopPropagation()}>
                      <LeadContactButtons leadId={l.id} mobile={l.mobile} company={l.companyName} contactName={l.contactName} />
                      <span className="text-[10px] text-gray-400 truncate max-w-[110px]">{l.ownerName ?? 'Unassigned'}</span>
                    </div>
                  </div>
                ))}
                {col.leads.length === 0 && <div className="text-xs text-gray-400 text-center py-6">Empty</div>}
                {col.hasMore && <div className="text-[11px] text-center text-gray-400 py-1">Showing 50 of {col.count} — narrow the filters to see the rest</div>}
              </div>
            </div>
          ))}
          {!(data?.columns ?? []).length && (
            <div className="text-sm text-gray-400 py-10">No statuses configured yet — add them under Leads → Settings.</div>
          )}
        </div>
      )}

      {detailId && <LeadDetailDrawer leadId={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}
